import PublicLayout from '@/layouts/PublicLayout'
import SeoHead from '@/components/SeoHead'

export default function TermsPage() {
  return (
    <>
      <SeoHead
        title="Terms of Service — LendSolo"
        description="The terms that govern your use of LendSolo."
        canonicalPath="/terms"
        ogType="article"
      />

      <PublicLayout
        title="Terms of Service"
        description="Last updated April 20, 2026"
      >
        <div
          className="prose prose-gray max-w-none
            prose-headings:font-bold prose-headings:text-gray-900 prose-headings:scroll-mt-16
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:leading-relaxed prose-p:text-gray-700
            prose-li:text-gray-700
            prose-a:text-emerald-600 prose-a:font-medium hover:prose-a:text-emerald-700
            prose-strong:text-gray-900"
        >
          <h2 id="agreement">1. Agreement to these Terms</h2>
          <p>
            These Terms of Service ("<strong>Terms</strong>") are a legal agreement between
            you and Lendsolo LLC, an Illinois limited liability company ("<strong>LendSolo</strong>,"
            "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"). They
            govern your access to and use of lendsolo.com and the LendSolo software-as-a-service
            platform, including any related features, tools, and companion products such as
            DebtLens (collectively, the "<strong>Services</strong>").
          </p>
          <p>
            By creating an account, subscribing to a paid plan, or otherwise using the
            Services, you agree to be bound by these Terms. If you do not agree, do not use
            the Services.
          </p>
          <p>
            If you are accessing the Services on behalf of an entity, you represent that you
            have authority to bind that entity, and "you" refers to both you personally and
            that entity.
          </p>
          <p>
            We may update these Terms from time to time. When we do, we will revise the
            "Last updated" date above, and for material changes we will provide reasonable
            notice (typically by email and in-app notice) before the change takes effect.
            Your continued use of the Services after the effective date constitutes
            acceptance of the updated Terms.
          </p>

          <h2 id="services">2. The Services</h2>
          <p>
            LendSolo provides a cloud-based loan portfolio management platform for
            independent private lenders and micro-lending businesses. Features may include
            loan tracking, payment recording, amortization schedules, borrower records, tax
            document generation (such as IRS Form 1098), investor participation tracking,
            distribution calculations, deal offerings, and related tools. Available features
            depend on your subscription tier.
          </p>

          <h3>What the Services are not</h3>
          <p>
            <strong>
              LendSolo is a record-keeping and tracking tool. It is not a source of
              financial, legal, tax, investment, lending, or regulatory advice.
            </strong>{' '}
            Outputs generated by the Services — including amortization schedules, interest
            calculations, tax forms, risk narratives, deal memos, investor distributions,
            and any AI-generated content — are provided for your informational use. You are
            solely responsible for the accuracy, legal compliance, and regulatory
            suitability of any decision you make or action you take based on the Services.
            You should consult qualified professionals (attorneys, CPAs, licensed lending
            advisors) regarding your specific situation.
          </p>
          <p>
            LendSolo is not a lender, broker, servicer, fund administrator, transfer agent,
            registered investment adviser, or financial institution. We do not originate,
            underwrite, service, fund, or guarantee any loan. We do not provide investment
            advice or recommendations.
          </p>

          <h3>AI-powered features</h3>
          <p>
            Certain features (such as deal memo generation and risk narratives, available on
            higher tiers) use third-party large language models, including the Anthropic
            API, to process data you submit. These outputs are model-generated and may
            contain errors, omissions, or inaccuracies. You are responsible for reviewing
            and verifying any AI-generated content before relying on it.
          </p>

          <h2 id="accounts">3. Accounts</h2>
          <p>To use most features you must create an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>
              Keep your login credentials confidential and not share them with any
              unauthorized person
            </li>
            <li>Be responsible for all activity that occurs under your account</li>
            <li>
              Notify us immediately at{' '}
              <a href="mailto:support@lendsolo.com">support@lendsolo.com</a> if you suspect
              unauthorized access
            </li>
          </ul>
          <p>
            You must be at least 18 years old and legally capable of entering into a binding
            contract. You may not create an account using automated means or under a false
            or borrowed identity.
          </p>

          <h3>Team members, investors, and borrowers</h3>
          <p>
            Where your subscription permits you to invite additional users (such as team
            members on the Fund tier, or investors and borrowers via magic link), you are
            responsible for those users' compliance with these Terms in connection with your
            account. You represent that you have the authority and consent required under
            applicable law to provide their information to LendSolo.
          </p>

          <h2 id="billing">4. Subscriptions and billing</h2>

          <h3>Plans and pricing</h3>
          <p>
            LendSolo is offered on monthly or annual subscription plans at the prices posted
            at lendsolo.com/pricing. We may change pricing at any time; changes affecting
            existing subscriptions will take effect at the start of the next renewal period
            following reasonable notice.
          </p>

          <h3>Payment</h3>
          <p>
            Subscriptions are billed in advance on a recurring basis. Payment is processed
            by Stripe, Inc. under Stripe's own terms and privacy policy. By subscribing, you
            authorize us (through Stripe) to charge your payment method for the applicable
            subscription fees, including any applicable taxes, until you cancel.
          </p>

          <h3>Free trials</h3>
          <p>
            We may offer free trials of paid tiers. Trials automatically convert to paid
            subscriptions at the end of the trial unless cancelled before the trial ends. We
            may require a payment method at trial signup.
          </p>

          <h3>Cancellation and refunds</h3>
          <p>
            You may cancel your subscription at any time through your account settings.
            Cancellation takes effect at the end of your current billing period; you retain
            access until then.{' '}
            <strong>
              Fees already paid are non-refundable, including for partial billing periods,
              unused features, or downgrades.
            </strong>{' '}
            This includes annual plans.
          </p>

          <h3>Failed payments</h3>
          <p>
            If a payment fails, we may suspend or downgrade access until payment is
            successful. We may terminate accounts with persistently unpaid balances after
            reasonable attempts to collect.
          </p>

          <h3>Taxes</h3>
          <p>
            Posted prices do not include taxes unless stated otherwise. You are responsible
            for any sales, use, VAT, or similar taxes applicable to your subscription.
          </p>

          <h2 id="acceptable-use">5. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>
              Use the Services in violation of any applicable law or regulation, including
              lending, securities, tax, consumer protection, and privacy laws applicable to
              your lending activity
            </li>
            <li>
              Use the Services to originate, track, or document loans or investments in
              jurisdictions where doing so would violate applicable law
            </li>
            <li>
              Upload data you do not have the right to upload, or that violates any third
              party's privacy or intellectual property rights
            </li>
            <li>
              Attempt to probe, scan, or test the vulnerability of the Services, or breach
              any security or authentication measure
            </li>
            <li>
              Reverse engineer, decompile, or attempt to extract the source code of the
              Services, except to the extent applicable law prohibits such a restriction
            </li>
            <li>Use the Services to build a competing product or scrape data in bulk</li>
            <li>
              Interfere with, disrupt, or create an undue burden on the Services or on
              other users
            </li>
            <li>
              Resell, sublicense, or redistribute access to the Services without our written
              permission
            </li>
            <li>Use the Services to transmit malware, spam, or unsolicited communications</li>
            <li>
              Impersonate another person or misrepresent your affiliation with any person or
              entity
            </li>
          </ul>
          <p>
            We may investigate and take action against any suspected violation, including
            suspending or terminating accounts, at our sole discretion.
          </p>

          <h2 id="your-data">6. Your data</h2>

          <h3>Your ownership</h3>
          <p>
            You retain all rights in the data you upload to the Services, including loan
            records, borrower information, payment history, and any other content you
            submit ("<strong>Your Data</strong>"). You grant LendSolo a limited,
            non-exclusive license to host, process, transmit, display, and back up Your
            Data solely to provide the Services to you.
          </p>

          <h3>Our use of Your Data</h3>
          <p>
            We process Your Data in accordance with our{' '}
            <a href="/privacy">Privacy Policy</a>. We do not sell Your Data and do not
            share it with third parties except as described in the Privacy Policy (for
            example, with subprocessors like Stripe, Resend, Render, and Anthropic to
            operate the Services).
          </p>

          <h3>Backups and data loss</h3>
          <p>
            We perform regular backups but do not guarantee that backups will be complete,
            current, or recoverable in all circumstances. You are responsible for
            maintaining your own copies of critical data.
          </p>

          <h3>Data export and deletion</h3>
          <p>
            You may export Your Data at any time through the Services. On account
            termination, we will retain Your Data for up to ninety (90) days to allow for
            export or reactivation, after which we will delete it in accordance with our
            Privacy Policy, subject to legal retention requirements.
          </p>

          <h2 id="ip">7. Intellectual property</h2>
          <p>
            The Services, including all software, source code, databases, designs,
            interfaces, trademarks, logos, and content (other than Your Data) are owned by
            LendSolo or its licensors and are protected by copyright, trademark, and other
            intellectual property laws. Subject to your compliance with these Terms, we
            grant you a limited, non-exclusive, non-transferable, revocable right to access
            and use the Services for your internal business purposes.
          </p>
          <p>
            No rights are granted to you other than those expressly stated in these Terms.
            All feedback, suggestions, and ideas you provide to us regarding the Services
            may be used by LendSolo without obligation or compensation to you.
          </p>

          <h2 id="disclaimers">8. Disclaimers</h2>
          <p>
            <strong>
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
              ANY KIND, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, LENDSOLO
              DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ACCURACY.
            </strong>
          </p>
          <p>
            Without limiting the foregoing, LendSolo does not warrant that the Services
            will be uninterrupted, error-free, or secure; that outputs will be accurate or
            complete; or that any defects will be corrected. You assume all responsibility
            for your decisions and actions based on the Services.
          </p>
          <p>
            <strong>
              LendSolo is not providing legal, financial, tax, investment, or regulatory
              advice through the Services. Tax forms (including IRS Form 1098), amortization
              schedules, risk narratives, deal memos, and AI-generated content are
              informational tools only.
            </strong>{' '}
            You are responsible for ensuring that your lending activity, tax reporting, and
            any investor communications comply with applicable law.
          </p>

          <h2 id="liability">9. Limitation of liability</h2>
          <p>
            <strong>
              TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL LENDSOLO, ITS
              MEMBERS, MANAGERS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY
              LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING
              FROM OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICES, EVEN IF LENDSOLO
              HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </strong>
          </p>
          <p>
            <strong>
              LENDSOLO'S TOTAL CUMULATIVE LIABILITY ARISING FROM OR RELATED TO THESE TERMS
              OR THE SERVICES, REGARDLESS OF THE LEGAL THEORY, WILL NOT EXCEED ONE HUNDRED
              U.S. DOLLARS (US$100).
            </strong>
          </p>
          <p>
            Some jurisdictions do not allow the exclusion or limitation of certain damages.
            In those jurisdictions, our liability is limited to the maximum extent permitted
            by law.
          </p>

          <h2 id="indemnification">10. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless LendSolo and its members,
            managers, employees, and agents from and against any claims, damages,
            liabilities, losses, and reasonable attorneys' fees arising out of or related
            to: (a) your use of the Services; (b) Your Data or any content you upload or
            transmit; (c) your violation of these Terms; (d) your violation of any law or
            any third party's rights (including intellectual property, privacy, and
            consumer protection rights of borrowers and investors); or (e) any lending,
            securities, or tax activity you conduct using the Services.
          </p>

          <h2 id="termination">11. Termination</h2>
          <p>
            You may terminate your account at any time by canceling your subscription and
            closing your account through your settings or by contacting{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>
          <p>
            We may suspend or terminate your access to the Services, with or without cause,
            upon <strong>thirty (30) days' written notice</strong> (email to the address
            on your account is sufficient). We may suspend or terminate access immediately
            and without notice if we reasonably believe you have violated Section 5
            (Acceptable Use), engaged in fraud, failed to pay, or created a security,
            legal, or reputational risk to LendSolo or other users.
          </p>
          <p>
            On termination: your right to use the Services ends immediately; any
            outstanding fees become due; previously paid fees are non-refundable; and
            Sections 6 (Your Data), 7 (Intellectual Property), 8–10 (Disclaimers,
            Limitation of Liability, Indemnification), 12 (Dispute Resolution), and 13
            (Miscellaneous) survive.
          </p>

          <h2 id="disputes">
            12. Dispute resolution — mandatory arbitration and class waiver
          </h2>
          <p>
            <strong>
              Please read this section carefully. It affects your legal rights, including
              your right to file a lawsuit in court and to participate in a class action.
            </strong>
          </p>

          <h3>Informal resolution first</h3>
          <p>
            Before filing any formal claim, you agree to contact us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a> and attempt to
            resolve the dispute informally for at least{' '}
            <strong>sixty (60) days</strong>. Most disputes can be resolved this way.
          </p>

          <h3>Binding arbitration</h3>
          <p>
            If informal resolution fails,{' '}
            <strong>
              any dispute, claim, or controversy arising out of or relating to these Terms
              or the Services will be resolved by binding individual arbitration
            </strong>{' '}
            administered by the American Arbitration Association (AAA) under its Commercial
            Arbitration Rules and, where applicable, its Consumer Arbitration Rules. The
            arbitration will be conducted by a single arbitrator. The seat of arbitration
            will be <strong>Cook County, Illinois</strong>, and the language of the
            arbitration will be English. Judgment on the award may be entered in any court
            of competent jurisdiction.
          </p>
          <p>
            Each party will bear its own attorneys' fees and costs, except as the
            arbitrator may otherwise award under applicable law. Filing fees will be
            allocated under the applicable AAA rules.
          </p>

          <h3>Class action waiver</h3>
          <p>
            <strong>
              You and LendSolo agree that any arbitration or other proceeding will be
              conducted only on an individual basis. Neither party may bring a claim as a
              plaintiff or class member in any class, collective, consolidated, or
              representative action. The arbitrator may not consolidate claims or preside
              over any form of representative proceeding.
            </strong>
          </p>

          <h3>Exceptions</h3>
          <p>
            Nothing in this Section 12 prevents either party from: (a) seeking injunctive
            or other equitable relief in a court of competent jurisdiction in Cook County,
            Illinois to protect intellectual property rights or prevent unauthorized access
            to the Services; (b) bringing an individual action in small claims court; or
            (c) filing a complaint with any government agency that has jurisdiction.
          </p>

          <h3>30-day opt-out</h3>
          <p>
            You may opt out of this arbitration provision by sending written notice to{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a> within thirty
            (30) days of first accepting these Terms. Your notice must include your name,
            the email on your account, and a clear statement that you are opting out of
            arbitration. Opting out will not affect any other provision of these Terms.
          </p>

          <h3>Governing law</h3>
          <p>
            These Terms are governed by the laws of the State of Illinois, without regard
            to conflict-of-law rules. For any dispute not subject to arbitration, the state
            and federal courts located in Cook County, Illinois will have exclusive
            jurisdiction, and you consent to personal jurisdiction and venue in those
            courts.
          </p>

          <h2 id="miscellaneous">13. Miscellaneous</h2>
          <p>
            <strong>Entire agreement.</strong> These Terms, together with our{' '}
            <a href="/privacy">Privacy Policy</a> and{' '}
            <a href="/cookies">Cookie Policy</a>, are the entire agreement between you and
            LendSolo regarding the Services, and supersede any prior agreements.
          </p>
          <p>
            <strong>Severability.</strong> If any provision of these Terms is held
            unenforceable, that provision will be modified to the minimum extent necessary
            to make it enforceable, and the remaining provisions will remain in full
            effect.
          </p>
          <p>
            <strong>No waiver.</strong> Our failure to enforce any provision is not a
            waiver of our right to enforce it later.
          </p>
          <p>
            <strong>Assignment.</strong> You may not assign these Terms without our written
            consent. We may assign these Terms in connection with a merger, acquisition,
            financing, or sale of assets.
          </p>
          <p>
            <strong>Force majeure.</strong> Neither party is liable for failure to perform
            due to causes beyond reasonable control (natural disasters, war, government
            action, internet or utility outages, third-party service failures, etc.).
          </p>
          <p>
            <strong>Independent contractors.</strong> Nothing in these Terms creates a
            partnership, joint venture, employment, or agency relationship.
          </p>
          <p>
            <strong>Electronic communications.</strong> You consent to receive
            communications from us electronically, including email and in-app notices, and
            agree that electronic signatures and records satisfy any requirement for a
            writing or signature.
          </p>
          <p>
            <strong>Notices to us.</strong> Legal notices should be sent to{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>

          <h2 id="contact">14. Contact</h2>
          <p>
            Questions about these Terms? Email{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>
          <p>Lendsolo LLC</p>
          <p>An Illinois limited liability company</p>
        </div>
      </PublicLayout>
    </>
  )
}
